import React, { useEffect, useMemo, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useGroupUserRoles, useOrganizationId } from '@features/user';
import { Box, Link, Stack, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { hasRole, ROLES } from '@utils/roleUtils';

import { useGetSemanticContainerQuery } from '../store/api';

import { SemanticTileGrid } from './SemanticTileGrid';
import { TileSkeleton } from './TileSkeleton';

const HOME_TILE_LIMIT = 3;

/** Fisher–Yates copy — rotates the order without mutating the source list. */
const shuffled = <T,>(items: readonly T[]): T[] => {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

/**
 * The "practice metrics" strip embedded on the practice-owner home.
 * Rendered only for practice_administrator; shows a curated few tiles + a link
 * into the full /metrics dashboard. The home page wires it in unconditionally,
 * and this component enforces the practice-administrator restriction.
 */
export const SemanticMetricsHomeSection: React.FC = () => {
  const { practiceMetrics } = useEventTracking();
  const isPracticeAdmin = hasRole(useGroupUserRoles(), ROLES.PRACTICE_ADMINISTRATOR);
  const organizationId = useOrganizationId() ?? undefined;
  const { data, isLoading } = useGetSemanticContainerQuery(
    { organizationId, windowDays: 90 },
    { skip: !isPracticeAdmin || !organizationId },
  );

  // The backend hands us every data-bearing tile in a fixed (slug) order, so the
  // strip would always lead with the same three. Shuffle per load and take N.
  // Memoized on `data` → stable within a mount (no reshuffle on re-render),
  // re-rolls on the next visit. Filter to actual metric tiles first — the
  // container also carries the `semantic_below_gate` ghost, which belongs only on
  // the full /metrics page and must never land in the home strip.
  const tiles = useMemo(() => {
    const metricTiles = (data?.sections.flatMap(s => s.components) ?? []).filter(
      c => c.type_key === 'semantic_metric_tile',
    );
    return shuffled(metricTiles).slice(0, HOME_TILE_LIMIT);
  }, [data]);

  // Impression: fire once when the strip actually renders tiles to this admin.
  const shownRef = useRef(false);
  const visible = isPracticeAdmin && !isLoading && tiles.length > 0;
  useEffect(() => {
    if (visible && !shownRef.current) {
      shownRef.current = true;
      practiceMetrics.homeSectionShown({
        organization_id: organizationId,
        tile_count: tiles.length,
      });
    }
  }, [visible, tiles.length, organizationId, practiceMetrics]);

  if (!isPracticeAdmin) return null;
  // Nothing eligible yet — don't show an empty section on the home.
  if (!isLoading && tiles.length === 0) return null;

  return (
    <Box component="section">
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Practice metrics
        </Typography>
        <Link component={RouterLink} to="/metrics" sx={{ fontSize: 13, fontWeight: 600 }}>
          View all metrics →
        </Link>
      </Stack>

      {isLoading ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {[0, 1, 2].map(i => (
            <TileSkeleton key={i} />
          ))}
        </Box>
      ) : (
        <SemanticTileGrid components={tiles} entityId={organizationId} surface="home" />
      )}
    </Box>
  );
};
